import {
  Box,
  Input,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import type { SelectedIcdCode } from "./contractDiagnosisCodeForm";

interface Props {
  value: SelectedIcdCode | null;
  onChange: (value: SelectedIcdCode | null) => void;
}

function codeText(code: SelectedIcdCode) {
  return `${code.formattedDxCode} - ${code.shortDesc}`;
}

/**
 * ICD-10 search (`icd_10_codes/auto_complete`, as the classic modal and the medical-diagnosis
 * modal). Typing drops the current pick: a code counts only once chosen from the list.
 */
export function IcdCodeAutocomplete(props: Props) {
  const { queries } = useApi();
  const [text, setText] = React.useState(() => (props.value === null ? "" : codeText(props.value)));
  const [isOpen, setIsOpen] = React.useState(false);
  const searchTerm = useDebounce(text.trim(), 300);

  const search = useQuery({
    ...queries.patient.searchIcdCodes(searchTerm),
    enabled: isOpen && searchTerm.length > 0,
  });

  const handleTextChange = (next: string) => {
    setText(next);
    setIsOpen(next.trim().length > 0);
    if (props.value !== null) {
      props.onChange(null);
    }
  };

  const handleSelect = (code: SelectedIcdCode) => {
    props.onChange({
      formattedDxCode: code.formattedDxCode,
      id: code.id,
      shortDesc: code.shortDesc,
    });
    setText(codeText(code));
    setIsOpen(false);
  };

  const renderResults = () => {
    if (search.isError) {
      return (
        <Box color="red.500" fontSize="sm" p={3}>
          Could not get ICD codes
        </Box>
      );
    }
    if (search.isFetching || searchTerm !== text.trim()) {
      return (
        <Box p={3} textAlign="center">
          <Spinner size="sm" />
        </Box>
      );
    }
    if ((search.data ?? []).length === 0) {
      return (
        <Box color="gray.500" fontSize="sm" p={3} textAlign="center">
          No ICD codes found
        </Box>
      );
    }
    return (
      <List maxH="220px" overflowY="auto">
        {(search.data ?? []).map((code) => (
          <ListItem
            key={code.id}
            _hover={{ bg: "gray.50" }}
            cursor="pointer"
            px={3}
            py={2}
            onClick={() => handleSelect(code)}
          >
            <Text fontSize="sm">{codeText(code)}</Text>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Popover
      autoFocus={false}
      isOpen={isOpen}
      matchWidth={true}
      placement="bottom-start"
      onClose={() => setIsOpen(false)}
    >
      <PopoverTrigger>
        <Input
          autoComplete="off"
          placeholder="Enter ICD code, or description"
          size="sm"
          value={text}
          onChange={(event) => handleTextChange(event.target.value)}
          onFocus={() => setIsOpen(props.value === null && text.trim().length > 0)}
          onKeyDown={(event) => {
            // Focus stays in the input, so Escape would reach (and close) the review modal
            if (event.key === "Escape" && isOpen) {
              event.stopPropagation();
              setIsOpen(false);
            }
          }}
        />
      </PopoverTrigger>
      {/* matchWidth sizes the popper; the content fills it (the theme default is 20rem) */}
      <PopoverContent width="100%">
        <PopoverBody p={0}>{renderResults()}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
